<template>
  <div>
    <h3 class="text-xl font-bold text-neutral-900">{{ $t('Privacy') }}</h3>
    <p class="text-neutral-600 mt-1">
      {{ $t('Control who can access your project (see the roadmap, boards, and ideas). Recommended for closed beta testing or internal projects.') }}
    </p>

    <div
      class="bg-orange-100 text-neutral-800 px-3 py-2 rounded mt-4 flex items-center"
      v-if="this.isIntercomConnected"
    >
      <svg
        class="w-6 h-6 flex-shrink-0 mr-4 ml-2 text-orange-900"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        ></path>
      </svg>
      <p>
        {{ $t('Your project is connected to an Intercom workspace. These privacy settings have no effect on our Intercom app. You can control who has access to the Intercom app in your Messenger settings.') }}
      </p>
    </div>

    <PrivacyAlert v-if="!privacyCheck && isPublished" :title="$t('Available on Business and higher plans')" :description="getAppropriateDescription" :link="'/edit?view=billing'" :click="$t('View plans')" />
    <PrivacyAlert v-if="!isPublished" :title="$t('Your board is a draft')" :description="$t('Your board is a draft while on trial. It goes live when you pick a plan.')" :link="'/edit?view=billing'" :click="$t('View plans')" />

    <h4 class="mt-8 font-semibold text-neutral-900">{{ $t('Your project') }}</h4>
    <p class="text-neutral-600 text-sm mt-1" v-if="!isPublished">{{ $t('This applies once the board is live.') }}</p>

    <div class="toggle-wrapper mt-1">
      <div
        class="button--toggle"
        :class="{ 'area--disabled': isPrivateToggleLoading }"
      >
        <input
          :disabled="!privacyCheck"
          type="checkbox"
          class="checkbox"
          @change="togglePrivacy()"
          v-model="isProjectPrivate"
        />
        <div class="knobs"></div>
        <div class="layer"></div>
      </div>
      <transition name="fromLeft" mode="out-in" appear>
        <span v-if="!isPrivateToggleLoading" key="loading1">
          <span
            v-if="isProjectPrivate"
            key="isprivate"
            class="flex items-center"
          >
            <svg
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span><strong>{{ $t('Private') }}</strong> – {{ $t('only allowed users can view') }}</span>
          </span>
          <span v-else key="ispublic" class="flex items-center">
            <svg
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z"
              ></path>
            </svg>
            <span><strong>{{ $t('Public') }}</strong> – {{ $t('anybody can view') }}</span>
          </span>
        </span>
        <span class="text-neutral-700" v-else key="loading2">{{ $t('Saving...') }}</span>
      </transition>
    </div>

    <h4 class="mt-8 font-semibold text-neutral-900">{{ $t('Search engines') }}</h4>
    <p class="text-neutral-600 mt-1">
      {{ $t('Choose whether search engines like Google can index your public board and show it in search results.') }}
    </p>

    <!-- Private boards are never exposed to search engines, so the toggle is moot. -->
    <div
      v-if="isProjectPrivate"
      class="bg-neutral-100 text-neutral-700 px-3 py-2 rounded mt-3 text-sm"
    >
      {{ $t('Private projects are never shown to search engines.') }}
    </div>

    <template v-else>
      <div class="toggle-wrapper mt-2" :class="{ 'area--disabled': !indexingAllowed }">
        <div
          class="button--toggle"
          :class="{ 'area--disabled': isIndexingToggleLoading }"
        >
          <input
            :disabled="!indexingAllowed"
            :checked="indexingAllowed && allowSearchEngines"
            type="checkbox"
            class="checkbox"
            @change="toggleSearchIndexing()"
          />
          <div class="knobs"></div>
          <div class="layer"></div>
        </div>
        <transition name="fromLeft" mode="out-in" appear>
          <span class="text-neutral-700" v-if="isIndexingToggleLoading" key="loading">{{ $t('Saving...') }}</span>
          <span v-else-if="!indexingAllowed" key="locked" class="text-neutral-700">{{ $t('Available on paid plans') }}</span>
          <span v-else-if="allowSearchEngines" key="on"><strong>{{ $t('On') }}</strong> – {{ $t('your board can appear in search results') }}</span>
          <span v-else key="off"><strong>{{ $t('Off') }}</strong> – {{ $t("search engines won't index your board") }}</span>
        </transition>
      </div>
      <p v-if="!isPublished" class="text-neutral-600 text-sm mt-2">{{ $t('Indexing starts once your board is live.') }}</p>
      <p v-else-if="!indexingAllowed" class="text-neutral-600 text-sm mt-2">
        <a href="/edit?view=billing" class="link">{{ $t('Upgrade your plan') }}</a>
        {{ $t('to let your board appear in search engines.') }}
      </p>
    </template>

    <div v-if="showSSOPrivacySetting">
      <h4 class="mt-4 font-semibold text-neutral-900">{{ $t('SSO Privacy') }}</h4>

      <div class="toggle-wrapper mt-1">
        <div
          class="button--toggle"
          :class="{ 'area--disabled': isPrivateToggleLoading }"
        >
          <input
            type="checkbox"
            class="checkbox"
            @change="toggleSSOPrivacy()"
            v-model="isSsoEnable"
          />
          <div class="knobs"></div>
          <div class="layer"></div>
        </div>
        <transition name="fromLeft" mode="out-in" appear>
          <span key="loading1">
            <span
              v-if="!isSsoEnable"
              key="isprivate"
              class="flex items-center"
            >
              <span>{{ $t('Only allowed users will be able to see private pages') }}</span>
            </span>
            <span v-else key="ispublic" class="flex items-center">
              <span>{{ $t('Allow all SSO authenticated users to see private pages') }}</span>
            </span>
          </span>
        </transition>
      </div>
    </div>

    <transition name="fromBottom" mode="out-in" appear>
      <div v-if="!isSsoEnable && (isProjectPrivate || !isPublished)">
        <template v-if="!isPublished">
          <h4 class="mt-8 font-semibold text-neutral-900">{{ $t('People who can see the draft') }}</h4>
          <p class="text-neutral-600 text-sm mt-1">{{ $t('Add the people who may see the draft. They sign in with that email and the board opens for them. Once the board is live, this becomes the Private allowlist.') }}</p>
        </template>
        <h4 class="mt-8 font-semibold text-neutral-900">
          {{ $t('Allowed email domains') }}
        </h4>
        <p class="text-neutral-600 mt-1">
          {{ $t('Only users with a following email address domains will have access to your project:') }}
        </p>

        <div class="mt-2 domain-list">
          <p
            class="mb-1 text-neutral-700 text-sm p-2 border border-dashed rounded-lg w-fit"
            v-if="projectAllowedDomains.length === 0 && !isDomainAdding"
          >
            {{ $t('No allowed domains yet') }}
          </p>

          <Tag
            v-for="domain in projectAllowedDomains"
            :key="domain.id"
            class="my-1 mr-2"
            @close="destroyDomain(domain)"
            >{{ domain.email_domain }}</Tag
          >

          <transition name="fromLeft" mode="out-in" appear>
            <p
              class="mb-1 text-neutral-700"
              v-if="isDomainRemoving || isDomainAdding"
            >
              {{ $t('Working...') }}
            </p>
          </transition>
        </div>

        <form action class="mt-2">
          <div class="flex items-center">
            <input
              type="text"
              class="input mr-2"
              :placeholder="$t('Example: company.com')"
              v-model="newDomain"
              :disabled="isDomainAdding"
            />
            <button
              type="submit"
              class="btn-main flex-shrink-0"
              @click.prevent="addDomain()"
              :disabled="isDomainAdding"
            >
              {{ $t('Add allowed domain') }}
            </button>
          </div>
          <p v-if="domainError.length > 1" class="text-red-500 text-sm mt-1">
            {{ domainError }}
          </p>
        </form>

        <h4 class="mt-8 font-semibold text-neutral-900">
          {{ $t('Allowed email addresses') }}
        </h4>
        <p class="text-neutral-600 mt-1">
          {{ $t('Only users with a following email addresses will have access to your project:') }}
        </p>

        <privacyTable :emails="projectAllowedEmails"></privacyTable>

      </div>
    </transition>
  </div>
</template>

<script>
import Tag from "../../components/tag";
import privacyTable from "./privacy_email_table";
import PrivacyAlert from "./plan_alert";
import {mapGetters} from "vuex"

export default {
  props: {
    projectPrivate: Boolean,
    allowedDomains: Array,
    allowedEmails: Array,
    isIntercomConnected: Boolean,
    subDomain: String,
    currentProject: Object,
  },
  data: function () {
    return {
      isProjectPrivate: this.projectPrivate,
      isPrivateToggleLoading: false,
      projectAllowedDomains: this.allowedDomains,
      projectAllowedEmails: this.allowedEmails,
      newDomain: "",
      domainError: "",
      isDomainAdding: false,
      isDomainRemoving: false,
      isSsoEnable: this.currentProject.sso_privacy,
      allowSearchEngines: this.currentProject.allow_search_engines,
      isIndexingToggleLoading: false,
      isPublished: !!this.currentProject.published_at,
    };
  },

  components: {
    privacyTable,
    Tag,
    PrivacyAlert,
  },
  computed:{
  ...mapGetters(['getPlanFeatures','getCurrentProjectPlan']),
    privacyCheck(){
      if(this.getCurrentProjectPlan.name !== "trial" && this.getCurrentProjectPlan.active_status){
        const plans = this.getPlanFeatures;
        return plans[this.getCurrentProjectPlan.name].privacy;
      }
      else{
        // A trial has no plan: Private is Business and up, the server enforces it.
        return false;
      }
    },
    getAppropriateDescription(){
      if(this.getCurrentProjectPlan.name!="trial"){
        if(this.getCurrentProjectPlan.active_status) return this.$t('Private projects are available on Business and higher plans.');
        else return this.$t('Your subscription failed to update. To make your project private, please update your card.')
      }
      else{
        return this.$t('Private projects are available on Business and higher plans.');
      }
    },
    showSSOPrivacySetting(){
      return (this.currentProject.sso_only && this.isProjectPrivate);
    },
    // Indexing is a paid feature. Unlike privacyCheck, trials do NOT get a pass —
    // this mirrors the server's subscription_valid_strict? gate so the UI never
    // promises indexing the backend won't actually deliver.
    indexingAllowed(){
      const plan = this.getCurrentProjectPlan;
      return !!(plan && plan.name !== "trial" && plan.active_status);
    }
  },
  methods: {

    togglePrivacy: function () {
      this.isPrivateToggleLoading = true;

      Rails.ajax({
        url: "/toggle_privacy",
        type: "POST",
        data: "",
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.isProjectPrivate = data.is_private;
          this.isPrivateToggleLoading = false;
          this.$emit("privacyUpdate", this.isProjectPrivate);
          this.$store.dispatch('getAllWarnings')
          this.$store.dispatch('fetchCurrentPlan')
        },
        error: (data, status) => {
          console.log(data);
          this.isPrivateToggleLoading = false;
        },
      });
    },

    toggleSSOPrivacy(){
      Rails.ajax({
        url: "/toggle_sso_privacy",
        type: "POST",
        data: "",
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.isSsoEnable = data.sso_privacy
          this.$store.dispatch('getAllWarnings')
          this.$store.dispatch('fetchCurrentPlan')
        },
        error: (data, status) => {
          console.log(data);
          this.isPrivateToggleLoading = false;
        },
      });
    },

    toggleSearchIndexing: function () {
      this.isIndexingToggleLoading = true;

      Rails.ajax({
        url: "/toggle_search_indexing",
        type: "POST",
        data: "",
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.allowSearchEngines = data.allow_search_engines;
          this.isIndexingToggleLoading = false;
        },
        error: (data, status) => {
          console.log(data);
          this.isIndexingToggleLoading = false;
        },
      });
    },

    isDomainValid: function () {
      // Not perfect, but good enough to prevent email addresses and typos
      return /^[\w-]+\.[\.\w+-]*\w$/.test(this.newDomain);
    },

    addDomain: function () {
      this.domainError = "";

      if (!this.isDomainValid()) {
        this.domainError = this.$t('Not a valid domain. Please enter a domain like "feedbear.com"');
        return;
      }

      this.isDomainAdding = true;
      var params = new FormData();
      params.append("email_domain", this.newDomain);

      Rails.ajax({
        url: "/invited_customers",
        type: "POST",
        data: params,
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.newDomain = "";
          this.projectAllowedDomains.push(data);
          this.isDomainAdding = false;
        },
        error: (data, status) => {
          console.log(data);
          this.isDomainAdding = false;
        },
      });
    },

    destroyDomain: function (domain) {
      this.isDomainRemoving = true;

      Rails.ajax({
        url: "/invited_customers/" + domain.id,
        type: "DELETE",
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.projectAllowedDomains.splice(
            this.projectAllowedDomains.indexOf(domain),
            1
          );
          this.isDomainRemoving = false;
        },
        error: (data, status) => {
          console.log(data);
          this.isDomainRemoving = false;
        },
      });
    },
  },
};
</script>

<style scoped>
.domain-list {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
}

.icon--inline {
  display: inline;
  position: relative;
  top: 2px;
}

.area--disabled {
  opacity: 0.4;
  pointer-events: none;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
}

.button--toggle {
  position: relative;
  width: 74px;
  height: 36px;
  overflow: hidden;
  margin-right: 0.5rem;
}

.button--toggle,
.button--toggle .layer {
  border-radius: 100px;
}

.checkbox {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 3;
}

.knobs,
.layer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.knobs {
  z-index: 2;
}

.layer {
  width: 100%;
  background-color: #f5f5f5;
  transition: 0.3s ease all;
  z-index: 1;
  border: 1px solid gainsboro;
}

.button--toggle .knobs:before {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 28px;
  height: 28px;
  background-color: #d2d2d2;
  border-radius: 50%;
  transition: 0.3s ease all, left 0.3s cubic-bezier(0.18, 0.89, 0.35, 1.15);
}

.button--toggle .checkbox:active + .knobs:before {
  width: 46px;
  border-radius: 100px;
}

.button--toggle .checkbox:checked:active + .knobs:before {
  margin-left: -26px;
}

.button--toggle .checkbox:checked + .knobs:before {
  content: "";
  left: 42px;
  background-color: #3bd087;
}

.button--toggle .checkbox:checked ~ .layer {
  background-color: #d1f0e1;
  border-color: #a3e6c6;
}

/* Animations */

.fromLeft-move {
  transition: all 250ms;
}
.fromLeft-enter-active,
.fromLeft-leave-active {
  transition: all 250ms;
}
.fromLeft-enter,
.fromLeft-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
</style>
